import { useEffect, useMemo, useState } from 'react'
import { Box, Drawer, Grid, InputAdornment, Tab, Tabs, TextField } from '@mui/material'
import { omit } from 'lodash'
import RoleList from './RoleList'
import RoleEdit from './RoleEdit'
import ModuleList from './ModuleList'

import { useDispatch, useSelector } from 'react-redux'
import * as service from 'store/settings/role'
import { useParams, useNavigate } from 'react-router-dom'
import Loading from 'components/Loading'
import { BasePathProvider } from 'components/Providers'
import PageHeader from 'components/AppBar/PageHeader'
import RoleCompare from './RoleCompare'
import { SearchOutlined as SearchIcon } from '@mui/icons-material'
import { CloseIcon } from 'components/Icons'

function Role() {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const districtTypes = useSelector(state => state.lookup.types?.filter(t => t.category === 'districtType') || [])
  const [activeTab, setActiveTab] = useState(0)
  const [districtType, setDistrictType] = useState()
  const [editOpen, setEditOpen] = useState(false)
  const [editRole, setEditRole] = useState(null)
  const [permissionSearch, setPermissionSearch] = useState('')

  const { count, search: roles } = useSelector(state => state.settings.role)
  const [role, setRole] = useState(null)

  const effectiveDistrictType = districtType || 'provider'

  useEffect(() => {
    if (!districtType) handleDistrictTypeChange('provider')
  }, [districtType])

  const filteredRoles = useMemo(
    () => roles?.filter(r => r.types?.includes(effectiveDistrictType)),
    [roles, effectiveDistrictType, districtType]
  )

  useEffect(() => {
    if (filteredRoles?.length && !id) {
      navigate(`/permission/${filteredRoles[0].id}`)
    }
  }, [filteredRoles, id])

  useEffect(() => {
    const initialize = async () => {
      if (count === null) await dispatch(service.search())
      if (id) {
        const { error, payload } = await dispatch(service.get({ id: parseInt(id) || 0 }))
        if (!error && payload) setRole(payload)
      } else {
        setRole(null)
      }
    }
    initialize()
  }, [dispatch, id, count, districtType])

  useEffect(() => { setPermissionSearch('') }, [id])

  const handleDistrictTypeChange = newType => {
    setDistrictType(newType)
    navigate('/permission')
  }

  if (!roles) return <Loading />

  return (
    <BasePathProvider value="/permission">
      <Box sx={{ mb: 2 }}>
        <PageHeader title={'Permissions'} add />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <RoleList
            roles={filteredRoles}
            districtType={districtType}
            districtTypes={districtTypes}
            onDistrictTypeChange={handleDistrictTypeChange}
            onEditClick={r => { setEditRole(r ?? null); setEditOpen(true) }}
          />
        </Grid>

        {!role && (
          <Grid item xs={12} md={9}>
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary', bgcolor: 'background.paper', borderRadius: 1 }}>
              Select a role from the sidebar to view its permissions, or click New Role.
            </Box>
          </Grid>
        )}

        {districtType && role && (
          <Grid item xs={12} md={9}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, backgroundColor: 'background.paper', p: 1, borderRadius: 1 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ '& .MuiTab-root': { fontWeight: 600 } }}>
                <Tab label="Permissions" />
                <Tab label="Compare Roles" />
              </Tabs>
            </Box>

            {activeTab === 0 && role.modules && (
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, backgroundColor: 'background.paper', p: 1, borderRadius: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search permissions…"
                    value={permissionSearch}
                    onChange={e => setPermissionSearch(e.target.value)}
                    sx={{ width: '100%' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                           <CloseIcon fontSize="small" sx={{ cursor: 'pointer', visibility: permissionSearch ? 'visible' : 'hidden' }} onClick={() => setPermissionSearch('')} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <ModuleList
                  role={omit(role, 'modules')}
                  modules={role.modules}
                  districtType={districtType}
                  search={permissionSearch}
                />
              </Box>
            )}

            {activeTab === 1 && (
              <RoleCompare availableRoles={filteredRoles} selectedRole={role} districtType={districtType} />
            )}
          </Grid>
        )}
      </Grid>

      <Drawer
        anchor="right"
        open={editOpen}
        onClose={() => setEditOpen(false)}
        PaperProps={{ sx: { width: 420 } }}
      >
        <RoleEdit role={editRole} onClose={() => setEditOpen(false)} />
      </Drawer>
    </BasePathProvider>
  )
}

export default Role
